/**
 * On-load & occasional browser push permission prompt.
 *
 * Complements the contextual `PushEnablePrompt` (Watchlist / Your Desk). Where that
 * one asks at a moment of intent, this one asks on first authenticated load and
 * then occasionally — a random interval between 7 and 30 days — for users who
 * still haven't enabled push.
 *
 * It reuses `usePushNotifications()` so it shares the exact same `enable()` path
 * (the native `Notification.requestPermission()` popup + `/push-sw.js` registration)
 * and the same environment gating. Nothing here fires in dev, inside the Lovable
 * preview iframe, on `?sw=off`, or on preview hosts — so this prompt is only ever
 * observable in the published app, exactly like the contextual prompt.
 *
 * Browser constraint that drives the design:
 * - `permission === 'default'` → we CAN show the native popup (real re-prompt).
 * - `permission === 'denied'` → the browser blocks the popup forever until the
 * user flips it back in site settings. We can only
 * show an in-app "re-enable in settings" nudge.
 * - `permission === 'granted'` → nothing to ask; never prompt.
 */
import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'push-onload-prompt',
  label: 'Turn on push notifications (on load)',
  description:
    'Asks the user to enable browser push on first visit and occasionally thereafter (random 7–30 day interval) if they still have not enabled it. Only fires in the published app.',
  triggerType: 'panel',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

/** Days, inclusive of both bounds: 7..30. Exported for unit tests. */
export const RE_PROMPT_MIN_DAYS = 7;
export const RE_PROMPT_MAX_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** random integer-of-days window in ms, 7..30 inclusive. Exported for tests. */
export function randomRePromptDelayMs(now = Date.now()): number {
  const span = RE_PROMPT_MAX_DAYS - RE_PROMPT_MIN_DAYS;
  const days = RE_PROMPT_MIN_DAYS + Math.random() * span;
  return Math.round(days * DAY_MS);
}

const LS_FIRST_SHOWN = 'cfx_push_onload:first_shown';
const LS_NEXT_PROMPT = 'cfx_push_onload:next_prompt';
const LS_DENIED_NEXT = 'cfx_push_onload:denied_next';

function readMs(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeMs(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* private mode — best effort, this session only */
  }
}

export interface OnloadPromptInput {
  /** environmentAllowsPush() && pushIsSupported() — already combined by the hook. */
  available: boolean;
  permission: NotificationPermission;
  deviceEnabled: boolean;
  now: number;
  firstShown: number | null;
  nextPrompt: number | null;
  deniedNext: number | null;
}

export interface OnloadPromptDecision {
  show: boolean;
  mode: 'default' | 'denied' | null;
}

/**
 * Pure scheduler. Given the current state, decide whether the on-load banner
 * should be visible right now and in which mode. Has no side effects — the
 * component is responsible for stamping the "next" timestamps once a banner is
 * actually shown (so repeated mounts within one session don't re-trigger it).
 */
export function resolveOnloadPrompt(input: OnloadPromptInput): OnloadPromptDecision {
  const { available, permission, deviceEnabled, now, firstShown, nextPrompt, deniedNext } = input;

  if (!available) return { show: false, mode: null };
  if (permission === 'granted' || deviceEnabled) return { show: false, mode: null };

  if (permission === 'default') {
    // First authenticated visit: nextPrompt is unset → ask immediately.
    if (nextPrompt === null) return { show: true, mode: 'default' };
    if (now >= nextPrompt) return { show: true, mode: 'default' };
    return { show: false, mode: null };
  }

  if (permission === 'denied') {
    // The native popup is blocked; we can only nudge toward site settings,
    // and we throttle that nudge on the same 7..30 day window so denied users
    // are never nagged harder than default-state users.
    if (deniedNext === null) return { show: true, mode: 'denied' };
    if (now >= deniedNext) return { show: true, mode: 'denied' };
    return { show: false, mode: null };
  }

  return { show: false, mode: null };
}

const PushOnloadPrompt = () => {
  useFeatureRegistration(FEATURE_META);
  const { available, permission, deviceEnabled, busy, enable } = usePushNotifications('app-load');

  // `hidden` lets the user dismiss the banner for this session without us
  // re-stamping the schedule — the next random window still brings it back.
  const [hidden, setHidden] = useState(false);

  const now = Date.now();
  const decision = resolveOnloadPrompt({
    available,
    permission,
    deviceEnabled,
    now,
    firstShown: readMs(LS_FIRST_SHOWN),
    nextPrompt: readMs(LS_NEXT_PROMPT),
    deniedNext: readMs(LS_DENIED_NEXT),
  });

  const visible = decision.show && !hidden;

  // The moment the banner actually renders, stamp the schedule so a later
  // mount in the same session (navigation, reload) doesn't re-trigger it. This
  // is what makes "occasionally" honest: shown once → silent for 7..30 days.
  useEffect(() => {
    if (!visible) return;
    writeMs(LS_FIRST_SHOWN, now);
    if (decision.mode === 'default') {
      writeMs(LS_NEXT_PROMPT, now + randomRePromptDelayMs(now));
    } else if (decision.mode === 'denied') {
      writeMs(LS_DENIED_NEXT, now + randomRePromptDelayMs(now));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, decision.mode]);

  if (!visible) return null;

  const dismiss = () => setHidden(true);

  if (decision.mode === 'denied') {
    return (
      <div className="relative rounded-lg border border-amber-200 bg-amber-50 p-4 pr-10 dark:border-amber-900/50 dark:bg-amber-950/30">
        <Button
          type="button"
          variant="quiet"
          size="icon-sm"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-1 top-1"
        >
          <X className="w-4 h-4" />
        </Button>
        <div className="flex items-start gap-2">
          <Bell className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Notifications are blocked for this site</p>
            <p className="text-xs text-muted-foreground">
              To get push alerts, re-enable notifications in your browser's site settings for cannafax.ai.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg border border-border bg-muted/40 p-4 pr-10">
      <Button
        type="button"
        variant="quiet"
        size="icon-sm"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-1 top-1"
      >
        <X className="w-4 h-4" />
      </Button>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <Bell className="w-4 h-4 mt-0.5 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Want to know the moment it happens?</p>
            <p className="text-xs text-muted-foreground">
              Get a push the moment a buyer reaches out or a watched listing changes.
            </p>
          </div>
        </div>
        <div className="sm:ml-auto shrink-0">
          <Button size="sm" disabled={busy} onClick={() => enable()}>
            Turn on push
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PushOnloadPrompt;
